'use client';

/*
 * Documentation:
 * Select — https://app.subframe.com/6fc7f5fbb098/library?component=Select_bb88f90b-8c43-4b73-9c2f-3558ce7838f3
 */
import { c as _c } from "react/compiler-runtime";
import React from 'react';
import { cn } from '../utils';
import { FeatherCheck, FeatherChevronDown, FeatherPlus, FeatherX } from '@subframe/core';
import * as SubframeCore from '@subframe/core';
import * as SelectPrimitive from '@radix-ui/react-select';
interface ContentProps extends React.ComponentProps<typeof SubframeCore.Select.Content> {
  children?: React.ReactNode;
  className?: string;
}
const Content = React.forwardRef<HTMLDivElement, ContentProps>(function Content(t0, ref) {
  const $ = _c(9);
  let children;
  let className;
  let otherProps;
  if ($[0] !== t0) {
    ({
      children,
      className,
      ...otherProps
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = otherProps;
  } else {
    children = $[1];
    className = $[2];
    otherProps = $[3];
  }
  let t1;
  if ($[4] !== children || $[5] !== className || $[6] !== otherProps || $[7] !== ref) {
    t1 = children ? <SelectPrimitive.Portal><SubframeCore.Select.Content asChild={true} position="popper" side="bottom" align="start" sideOffset={8} {...otherProps}><div className={cn("relative z-[10010] flex max-h-[var(--radix-select-content-available-height)] min-w-[var(--radix-select-trigger-width)] flex-col items-start overflow-y-auto rounded-[16px] border border-solid border-neutral-200 bg-white px-1 py-1 shadow-lg", className)} ref={ref}>{children}</div></SubframeCore.Select.Content></SelectPrimitive.Portal> : null;
    $[4] = children;
    $[5] = className;
    $[6] = otherProps;
    $[7] = ref;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  return t1;
});
interface ItemProps extends Omit<React.ComponentProps<typeof SubframeCore.Select.Item>, 'value'> {
  value: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}
const Item = React.forwardRef<HTMLDivElement, ItemProps>(function Item(t0, ref) {
  const $ = _c(18);
  let children;
  let className;
  let otherProps;
  let value;
  if ($[0] !== t0) {
    ({
      value,
      children,
      className,
      ...otherProps
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = otherProps;
    $[4] = value;
  } else {
    children = $[1];
    className = $[2];
    otherProps = $[3];
    value = $[4];
  }
  const t1 = value as string;
  let t2;
  if ($[5] !== className) {
    t2 = cn("group/969e345b data-[highlighted]:bg-brand-50 flex h-8 w-full cursor-pointer items-center gap-1 rounded-[8px] px-3 hover:bg-neutral-100 active:bg-neutral-50", className);
    $[5] = className;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const t3 = children || value;
  let t4;
  if ($[7] !== t3) {
    t4 = <Select.ItemText className="h-auto shrink-0 grow basis-0">{t3}</Select.ItemText>;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <FeatherCheck className="text-body font-body text-default-font group-data-[state=checked]/969e345b:text-brand-600 hidden group-hover/969e345b:hidden group-data-[state=checked]/969e345b:inline-flex" />;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== ref || $[11] !== t2 || $[12] !== t4) {
    t6 = <div className={t2} ref={ref}>{t4}{t5}</div>;
    $[10] = ref;
    $[11] = t2;
    $[12] = t4;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== otherProps || $[15] !== t1 || $[16] !== t6) {
    t7 = <SubframeCore.Select.Item value={t1} asChild={true} {...otherProps}>{t6}</SubframeCore.Select.Item>;
    $[14] = otherProps;
    $[15] = t1;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  return t7;
});
interface ItemTextProps extends React.ComponentProps<typeof SubframeCore.Select.ItemText> {
  children?: React.ReactNode;
  className?: string;
}
const ItemText = React.forwardRef<HTMLSpanElement, ItemTextProps>(function ItemText(t0, ref) {
  const $ = _c(9);
  let children;
  let className;
  let otherProps;
  if ($[0] !== t0) {
    ({
      children,
      className,
      ...otherProps
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = otherProps;
  } else {
    children = $[1];
    className = $[2];
    otherProps = $[3];
  }
  let t1;
  if ($[4] !== children || $[5] !== className || $[6] !== otherProps || $[7] !== ref) {
    t1 = children ? <SubframeCore.Select.ItemText {...otherProps}><span className={cn("text-body font-body text-default-font", className)} ref={ref}>{children}</span></SubframeCore.Select.ItemText> : null;
    $[4] = children;
    $[5] = className;
    $[6] = otherProps;
    $[7] = ref;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  return t1;
});
interface TriggerProps extends Omit<React.ComponentProps<typeof SubframeCore.Select.Trigger>, 'placeholder'> {
  placeholder?: React.ReactNode;
  icon?: React.ReactNode;
  hasTrailingControls?: boolean;
  className?: string;
}
const Trigger = React.forwardRef<HTMLButtonElement, TriggerProps>(function Trigger(t0, ref) {
  const $ = _c(21);
  let className;
  let otherProps;
  let placeholder;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      placeholder,
      icon: t1,
      hasTrailingControls: t2,
      className,
      ...otherProps
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = otherProps;
    $[3] = placeholder;
    $[4] = t1;
    $[5] = t2;
  } else {
    className = $[1];
    otherProps = $[2];
    placeholder = $[3];
    t1 = $[4];
    t2 = $[5];
  }
  const icon = t1 === undefined ? null : t1;
  const hasTrailingControls = t2 === undefined ? false : t2;
  const t3 = hasTrailingControls ? "pr-16" : "pr-10";
  let t4;
  if ($[6] !== className || $[7] !== t3) {
    t4 = cn("flex h-full w-full min-w-0 items-center gap-2 px-3 text-left", t3, className);
    $[6] = className;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== icon) {
    t5 = icon ? <SubframeCore.IconWrapper className="text-body font-body text-neutral-400">{icon}</SubframeCore.IconWrapper> : null;
    $[9] = icon;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const t6 = placeholder as string;
  let t7;
  if ($[11] !== t6) {
    t7 = <Select.TriggerValue placeholder={t6} />;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== ref || $[14] !== t4 || $[15] !== t5 || $[16] !== t7) {
    t8 = <button className={t4} ref={ref}>{t5}{t7}</button>;
    $[13] = ref;
    $[14] = t4;
    $[15] = t5;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== otherProps || $[19] !== t8) {
    t9 = <SubframeCore.Select.Trigger asChild={true} {...otherProps}>{t8}</SubframeCore.Select.Trigger>;
    $[18] = otherProps;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  return t9;
});
interface TriggerValueProps extends React.ComponentProps<typeof SubframeCore.Select.Value> {
  placeholder?: React.ReactNode;
  className?: string;
}
const TriggerValue = React.forwardRef<React.ElementRef<typeof SubframeCore.Select.Value>, TriggerValueProps>(function TriggerValue(t0, ref) {
  const $ = _c(11);
  let className;
  let otherProps;
  let placeholder;
  if ($[0] !== t0) {
    ({
      placeholder,
      className,
      ...otherProps
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = otherProps;
    $[3] = placeholder;
  } else {
    className = $[1];
    otherProps = $[2];
    placeholder = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("text-body font-body text-default-font min-w-0 flex-1 truncate", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== otherProps || $[7] !== placeholder || $[8] !== ref || $[9] !== t1) {
    t2 = <SubframeCore.Select.Value className={t1} ref={ref} placeholder={placeholder} {...otherProps}>Value</SubframeCore.Select.Value>;
    $[6] = otherProps;
    $[7] = placeholder;
    $[8] = ref;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
});
interface SelectRootProps extends React.ComponentProps<typeof SubframeCore.Select.Root> {
  disabled?: boolean;
  error?: boolean;
  variant?: 'outline' | 'filled';
  label?: React.ReactNode;
  placeholder?: React.ReactNode;
  helpText?: React.ReactNode;
  icon?: React.ReactNode;
  children?: React.ReactNode;
  value?: string;
  onValueChange?: (value: string) => void;
  clearable?: boolean;
  onClear?: () => void;
  selectedValueTitle?: string;
  className?: string;
  'aria-label'?: string;
}
const SelectRoot = React.forwardRef<HTMLDivElement, SelectRootProps>(function SelectRoot(t0, ref) {
  const $ = _c(77);
  let ariaLabel;
  let autoComplete;
  let children;
  let className;
  let defaultOpen;
  let defaultValue;
  let dir;
  let form;
  let helpText;
  let label;
  let name;
  let onClear;
  let onOpenChange;
  let onValueChange;
  let open;
  let otherProps;
  let placeholder;
  let required;
  let selectedValueTitle;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let value;
  if ($[0] !== t0) {
    ({
      disabled: t1,
      error: t2,
      variant: t3,
      label,
      placeholder,
      helpText,
      icon: t4,
      children,
      className,
      value,
      defaultValue,
      onValueChange,
      clearable: t5,
      onClear,
      selectedValueTitle,
      open,
      defaultOpen,
      onOpenChange,
      dir,
      name,
      autoComplete,
      required,
      form,
      "aria-label": ariaLabel,
      ...otherProps
    } = t0);
    $[0] = t0;
    $[1] = ariaLabel;
    $[2] = autoComplete;
    $[3] = children;
    $[4] = className;
    $[5] = defaultOpen;
    $[6] = defaultValue;
    $[7] = dir;
    $[8] = form;
    $[9] = helpText;
    $[10] = label;
    $[11] = name;
    $[12] = onClear;
    $[13] = onOpenChange;
    $[14] = onValueChange;
    $[15] = open;
    $[16] = otherProps;
    $[17] = placeholder;
    $[18] = required;
    $[19] = selectedValueTitle;
    $[20] = t1;
    $[21] = t2;
    $[22] = t3;
    $[23] = t4;
    $[24] = t5;
    $[25] = value;
  } else {
    ariaLabel = $[1];
    autoComplete = $[2];
    children = $[3];
    className = $[4];
    defaultOpen = $[5];
    defaultValue = $[6];
    dir = $[7];
    form = $[8];
    helpText = $[9];
    label = $[10];
    name = $[11];
    onClear = $[12];
    onOpenChange = $[13];
    onValueChange = $[14];
    open = $[15];
    otherProps = $[16];
    placeholder = $[17];
    required = $[18];
    selectedValueTitle = $[19];
    t1 = $[20];
    t2 = $[21];
    t3 = $[22];
    t4 = $[23];
    t5 = $[24];
    value = $[25];
  }
  const disabled = t1 === undefined ? false : t1;
  const error = t2 === undefined ? false : t2;
  const variant = t3 === undefined ? "outline" : t3;
  const icon = t4 === undefined ? null : t4;
  const clearable = t5 === undefined ? false : t5;
  const canClear = clearable && Boolean(value) && !disabled;
  let t6;
  if ($[26] !== className) {
    t6 = cn("group/bb88f90b flex cursor-pointer flex-col items-start gap-1", className);
    $[26] = className;
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  let t7;
  if ($[28] !== label) {
    t7 = label ? <span className="text-caption-bold font-caption-bold text-default-font">{label}</span> : null;
    $[28] = label;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  const t8 = variant === "filled";
  let t9;
  if ($[30] !== disabled || $[31] !== error || $[32] !== t8) {
    t9 = cn("bg-default-background group-focus-within/bb88f90b:border-brand-600 group-focus-within/bb88f90b:ring-brand-600/10 relative flex h-10 w-full flex-none flex-col items-start rounded-[8px] border border-solid border-neutral-300 transition-all duration-200 group-focus-within/bb88f90b:ring-4", {
      "border-neutral-200 bg-neutral-100 group-hover/bb88f90b:border-neutral-300 group-hover/bb88f90b:bg-neutral-100": t8,
      "border-error-600 group-focus-within/bb88f90b:border-error-600 group-focus-within/bb88f90b:ring-error-600/10": error,
      "cursor-not-allowed border-neutral-200 bg-neutral-50": disabled
    });
    $[30] = disabled;
    $[31] = error;
    $[32] = t8;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  let t10;
  if ($[34] !== ariaLabel || $[35] !== canClear || $[36] !== icon || $[37] !== placeholder || $[38] !== selectedValueTitle) {
    t10 = <Trigger placeholder={placeholder} icon={icon} hasTrailingControls={canClear} title={selectedValueTitle} aria-label={ariaLabel} />;
    $[34] = ariaLabel;
    $[35] = canClear;
    $[36] = icon;
    $[37] = placeholder;
    $[38] = selectedValueTitle;
    $[39] = t10;
  } else {
    t10 = $[39];
  }
  let t11;
  if ($[40] !== canClear || $[41] !== onClear) {
    t11 = canClear ? <button type="button" className="text-subtext-color hover:text-default-font absolute top-1/2 right-8 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full transition-colors hover:bg-neutral-100" onMouseDown={_temp} onClick={event_0 => {
      event_0.preventDefault();
      event_0.stopPropagation();
      onClear?.();
    }} aria-label={"Limpar sele\xE7\xE3o"}><FeatherX className="h-3.5 w-3.5" /></button> : null;
    $[40] = canClear;
    $[41] = onClear;
    $[42] = t11;
  } else {
    t11 = $[42];
  }
  let t12;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <FeatherChevronDown className="text-subtext-color pointer-events-none absolute top-1/2 right-3 h-4 w-4 -translate-y-1/2" />;
    $[43] = t12;
  } else {
    t12 = $[43];
  }
  let t13;
  if ($[44] !== t10 || $[45] !== t11 || $[46] !== t9) {
    t13 = <div className={t9}>{t10}{t11}{t12}</div>;
    $[44] = t10;
    $[45] = t11;
    $[46] = t9;
    $[47] = t13;
  } else {
    t13 = $[47];
  }
  let t14;
  if ($[48] !== error || $[49] !== helpText) {
    t14 = helpText ? <span className={cn("text-caption font-caption text-subtext-color", {
      "text-error-700": error
    })}>{helpText}</span> : null;
    $[48] = error;
    $[49] = helpText;
    $[50] = t14;
  } else {
    t14 = $[50];
  }
  let t15;
  if ($[51] !== children) {
    t15 = children ? <div className="flex w-full shrink-0 grow basis-0 flex-col items-start">{children}</div> : null;
    $[51] = children;
    $[52] = t15;
  } else {
    t15 = $[52];
  }
  let t16;
  if ($[53] !== t15) {
    t16 = <Content>{t15}</Content>;
    $[53] = t15;
    $[54] = t16;
  } else {
    t16 = $[54];
  }
  let t17;
  if ($[55] !== otherProps || $[56] !== ref || $[57] !== t13 || $[58] !== t14 || $[59] !== t16 || $[60] !== t6 || $[61] !== t7) {
    t17 = <div className={t6} ref={ref} {...otherProps}>{t7}{t13}{t14}{t16}</div>;
    $[55] = otherProps;
    $[56] = ref;
    $[57] = t13;
    $[58] = t14;
    $[59] = t16;
    $[60] = t6;
    $[61] = t7;
    $[62] = t17;
  } else {
    t17 = $[62];
  }
  let t18;
  if ($[63] !== autoComplete || $[64] !== defaultOpen || $[65] !== defaultValue || $[66] !== dir || $[67] !== disabled || $[68] !== form || $[69] !== name || $[70] !== onOpenChange || $[71] !== onValueChange || $[72] !== open || $[73] !== required || $[74] !== t17 || $[75] !== value) {
    t18 = <SubframeCore.Select.Root disabled={disabled} value={value} defaultValue={defaultValue} onValueChange={onValueChange} open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange} dir={dir} name={name} autoComplete={autoComplete} required={required} form={form}>{t17}</SubframeCore.Select.Root>;
    $[63] = autoComplete;
    $[64] = defaultOpen;
    $[65] = defaultValue;
    $[66] = dir;
    $[67] = disabled;
    $[68] = form;
    $[69] = name;
    $[70] = onOpenChange;
    $[71] = onValueChange;
    $[72] = open;
    $[73] = required;
    $[74] = t17;
    $[75] = value;
    $[76] = t18;
  } else {
    t18 = $[76];
  }
  return t18;
});
interface ActionProps {
  children?: React.ReactNode;
  onClick?: () => void;
  className?: string;
}
const Action = React.forwardRef<HTMLButtonElement, ActionProps>(function Action(t0, ref) {
  const $ = _c(10);
  const {
    children,
    onClick,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("text-caption-bold font-caption-bold text-brand-600 mt-1 flex w-full items-center gap-2 border-t border-solid border-neutral-100 px-3 py-2 transition-colors hover:bg-neutral-50 active:bg-neutral-100", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== onClick) {
    t2 = e => {
      e.preventDefault();
      e.stopPropagation();
      onClick?.();
    };
    $[2] = onClick;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <FeatherPlus className="h-4 w-4" />;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = children || "Criar novo";
  let t5;
  if ($[5] !== ref || $[6] !== t1 || $[7] !== t2 || $[8] !== t4) {
    t5 = <button type="button" className={t1} ref={ref} onClick={t2}>{t3}{t4}</button>;
    $[5] = ref;
    $[6] = t1;
    $[7] = t2;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
});
export const Select = Object.assign(SelectRoot, {
  Content,
  Item,
  ItemText,
  Trigger,
  TriggerValue,
  Action
});
function _temp(event) {
  event.preventDefault();
  event.stopPropagation();
}